import { useEffect, useState, useRef } from "react";
import "./styles.sass";
import assets from "../../assets";
import {
  WELCOME_CARD,
  LANGUAGE_OPTIONS,
  LANGUAGE_TN_STATE_BOARD,
} from "../../utils/constant";
import { Conversation } from "../conversation";
import useHandler from "./handler";
import { ToggleButton } from "../toggle-button";
import LiveKitVideoPlayer from "../../components/livekit-video";

const LANGUAGE_MAP = {
  English: "en-IN",
  Bengali: "bn-IN",
  Gujarati: "gu-IN",
  Hindi: "hi-IN",
  Kannada: "kn-IN",
  Malayalam: "ml-IN",
  Marathi: "mr-IN",
  Punjabi: "pa-IN",
  Tamil: "ta-IN",
  Telugu: "te-IN",
  Urdu: "ur-IN",
};

export const ChatScreen = () => {
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [fadeBubble, setFadeBubble] = useState(false);
  const chatBodyRef = useRef(null);
  const chatBottomRef = useRef(null);

  const {
    handleClearChat,
    handleNewChat,
    chapterRef,
    languageRef,
    conversation,
    selectedChapter,
    selectedSubject,
    selectedLanguage,
    openDropdown,
    inputText,
    classdetails,
    subjects,
    chapters,
    user,
    isStartEnabled,
    isEnable,
    isLoading,
    mode,
    selectedMedium,
    mediums,
    mediumRef,
    selectedLesson,
    selectedPart,
    selectedUnit,
    partRef,
    lessonRef,
    unitRef,
    exams,
    sections,
    units,
    isHistoryEmpty,
    onToggleBtn,
    getHistoryDetails,
    onClickStart,
    setSelectedChapter,
    setSelectedLanguage,
    setSelectedSubject,
    setOpenDropdown,
    setInputText,
    handleSend,
    handleKeyPress,
    onClickHistroy,
    onLiveAvatarApi,
    onEndAvatarInteraction,
    setSelectedMedium,
    setSelectedLesson,
    setSelectedPart,
    setSelectedUnit,
    avatarSession,
    avatarToast,
    isLiveAvatarLoading,
    downloadAvatarLog,
    lastAvatarSessionId,
  } = useHandler();

  const [isAvatarConnected, setIsAvatarConnected] = useState(false);

  useEffect(() => {
    if (!avatarSession) {
      setIsAvatarConnected(false);
    }
  }, [avatarSession]);

  useEffect(() => {
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (chatBodyRef.current) {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      });
    });
    return () => cancelAnimationFrame(raf1);
  }, [conversation, isLoading]);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.lang = LANGUAGE_MAP[selectedLanguage] || "en-IN";
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputText(transcript);
      if (mode) {
        handleSend(transcript);
      }
    };

    recognition.onerror = (event) => {
      console.log("Speech error", event);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  }, [selectedLanguage, setInputText, mode]);

  const handleMicClick = () => {
    const recognition = recognitionRef.current;

    if (!recognition) {
      alert("Speech not supported");
      return;
    }

    if (isListening) {
      recognition.stop();
      return;
    }

    try {
      recognition.start();
    } catch (e) {
      console.log("Already started");
    }
  };

  const lastUserMessage = conversation
    ?.filter((msg) => msg?.sender === "user")
    ?.pop()?.text;

  useEffect(() => {
    if (lastUserMessage) {
      setFadeBubble(false);
      const timer = setTimeout(() => {
        setFadeBubble(true);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [lastUserMessage]);

  const LiveAvatarView = () => (
    <section className="live_avatar_container">
      <section className="video_player_wrapper">
        {!avatarSession ? (
          <div className="live_avatar_loading_wrapper">
            <div className="tutor_logo_wrapper">
              <div className="wave_animation">
                <div className="wave"></div>
                <div className="wave"></div>
                <div className="wave"></div>
              </div>
              <img
                src={assets.ic_chat_moniter}
                alt="tutor_logo"
                className="tutor_img"
              />
            </div>
            <div className="loading_content">
              <p className="loading_text">Connecting to your AI tutor...</p>
              <p className="loading_subtitle">
                Setting up a secure video session...
              </p>
            </div>
            <div className="connecting_badge">
              <span className="badge_dot"></span>
              <span className="badge_text">Connecting...</span>
            </div>
          </div>
        ) : (
          <>
            <LiveKitVideoPlayer
              livekitUrl={avatarSession?.livekit_url}
              token={avatarSession?.livekit_client_token}
              onError={(err) => {
                console.error("Avatar streaming error:", err);
                onEndAvatarInteraction();
                alert("Connection to Avatar lost or invalid.");
              }}
              onConnected={() => setIsAvatarConnected(true)}
            />
            {lastUserMessage && (
              <div
                className={`last_question_bubble ${fadeBubble ? "fade_out" : ""}`}
              >
                <p>You: {lastUserMessage}</p>
              </div>
            )}
          </>
        )}
      </section>
      {isAvatarConnected && (
        <section className="avatar_input_footer">
          <section className="chat_input_wrapper">
            <input
              placeholder="Ask the avatar..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <section className="chat_footer_send_wrapper">
              <img
                src={assets.ic_mic}
                alt="ic_mic"
                className={`mic_icon ${isListening ? "listening" : ""}`}
                onClick={handleMicClick}
              />
              <img
                src={assets.ic_upload_btn}
                alt="ic_upload_btn"
                onClick={handleSend}
              />
            </section>
          </section>
        </section>
      )}
    </section>
  );

  const renderWelComeCard = (item, index) => {
    const { title, description, emoji, accent } = item || {};
    return (
      <section
        key={index}
        className={`welcome_card welcome_card_${accent || "blue"}`}
      >
        <div className="welcome_card_icon_wrap">
          <span className="welcome_card_emoji">{emoji}</span>
        </div>
        <h5>{title}</h5>
        <p className="welcome_card_desc">{description}</p>
      </section>
    );
  };

  return (
    <section className="chat_layout">
      {avatarToast && (
        <div className="avatar_toast avatar_toast_error">
          <span className="avatar_toast_dot"></span>
          {avatarToast.message}
        </div>
      )}
      {/* LEFT CONTEXT */}
      <section className="nav_context_contanier">
        <section className="nav_learn_title">
          <h4>Learning Context</h4>
          <h6>Set your topic to start learning</h6>
        </section>

        <section className="nav_subject_details">
          <section className="board_wrapper">
            {classdetails?.data?.board === "CBSE" ||
            classdetails?.data?.board === "TN_STATE_BOARD" ? (
              <>
                <section className="board">
                  <h5>Board</h5>
                  <h4>{classdetails?.data?.board}</h4>
                </section>
                <section className="class">
                  <h5>Class</h5>
                  <h4>{classdetails?.data?.class}</h4>
                </section>
              </>
            ) : (
              <section className="class">
                <h5>Competitive Exam</h5>
                <h4>{classdetails?.data?.class}</h4>
              </section>
            )}
          </section>
        </section>

        <section className="chat_nav_mode_wrapper">
          <section className="chat_nav_mode_container">
            <h4>Learn Mode</h4>
            <ToggleButton isToggle={mode} onToggle={onToggleBtn} />
            <h4>Teach Mode</h4>
          </section>
        </section>

        <section className="quiz_default_medium_wrapper">
          <h4>Medium</h4>
          <h5>{user?.data?.medium}</h5>
        </section>

        <section className="nav_subject_wrapper">
          {/* PARTS */}
          <section
            className="subject_wrapper"
            ref={partRef}
            onClick={() => setOpenDropdown("part")}
          >
            <h4>Exam</h4>
            <section className="drop_down_wrapper">
              <p>{selectedPart || "Select Exam"}</p>
              <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
            </section>
            {openDropdown === "part" && (
              <section className="options_list">
                {isLoading ? (
                  <p className="loading_text_dropdown">Loading exams...</p>
                ) : exams.length > 0 ? (
                  exams?.map((item, i) => (
                    <p
                      key={i}
                      className="option_item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPart(item);
                        setOpenDropdown(null);
                      }}
                    >
                      {item}
                    </p>
                  ))
                ) : (
                  <p className="no_data_text">No exams found</p>
                )}
              </section>
            )}
          </section>

          {/* UNITS */}
          <section
            className="subject_wrapper"
            ref={unitRef}
            onClick={() => setOpenDropdown("section")}
          >
            <h4>Section</h4>
            <section className="drop_down_wrapper">
              <p>{selectedUnit || "Select Section"}</p>
              <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
            </section>
            {openDropdown === "section" && (
              <section className="options_list">
                {!selectedPart ? (
                  <p className="info_text_dropdown">Please first Choose Part</p>
                ) : isLoading ? (
                  <p className="loading_text_dropdown">Loading sections...</p>
                ) : sections.length > 0 ? (
                  sections?.map((item, i) => (
                    <p
                      key={i}
                      className="option_item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUnit(item);
                        setOpenDropdown(null);
                      }}
                    >
                      {item}
                    </p>
                  ))
                ) : (
                  <p className="no_data_text">No sections found</p>
                )}
              </section>
            )}
          </section>
          
          {/* LESSONS */}
       <section
  className="subject_wrapper"
  ref={lessonRef}
  onClick={() => setOpenDropdown("subject")}
>
  <h4>Subjects</h4>

  <section className="drop_down_wrapper">
    <p>{selectedSubject || "Select Subject"}</p>

    <img
      src={assets.ic_chevron_down}
      alt="ic_chevron_down"
    />
  </section>

  {openDropdown === "subject" && (
    <section className="options_list">
      {selectedUnit.length === 0 ? (
        <p className="info_text_dropdown">
          Please first Choose Section
        </p>
      ) : isLoading ? (
        <p className="loading_text_dropdown">
          Loading subjects...
        </p>
      ) : subjects.length > 0 ? (
        subjects.map((item, i) => (
          <p
            key={i}
            className="option_item"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSubject(item);
              setOpenDropdown(null);
            }}
          >
            {item}
          </p>
        ))
      ) : (
        <p className="no_data_text">
          No subjects found
        </p>
      )}
    </section>
  )}
</section>

          {/* CHAPTER */}
          <section
            className="subject_wrapper"
            ref={chapterRef}
            onClick={() => setOpenDropdown("chapter")}
          >
            <h4>Chapter</h4>
            <section className="drop_down_wrapper">
              <section className="selected_pills_container">
                {selectedChapter.length > 0 ? (
                  selectedChapter.map((item, index) => (
                    <div
                      key={index}
                      className="selected_tag"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>{item}</span>
                      <img
                        src={assets.ic_close_white}
                        alt="remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedChapter((prev) =>
                            prev.filter((c) => c !== item),
                          );
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <p>Select Chapter</p>
                )}
              </section>
              <img
                src={assets.ic_chevron_down}
                alt="ic_chevron_down"
                className="chevron_icon"
              />
            </section>
            {openDropdown === "chapter" && (
              <section className="options_list">
                {!selectedSubject ? (
                  <p className="info_text_dropdown">Please first Choose Subject</p>
                ) : isLoading ? (
                  <p className="loading_text_dropdown">Loading chapters...</p>
                ) : chapters?.length > 0 ? (
                  [
                    "All Chapters",
                    ...chapters.filter((c) => c !== "All Chapters"),
                  ]?.map((item, i) => (
                    <section
                      key={i}
                      className="option_item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChapter((prev) => {
                          if (item === "All Chapters") {
                            return prev.includes("All Chapters")
                              ? []
                              : ["All Chapters"];
                          }
                          const filtered = prev.filter(
                            (c) => c !== "All Chapters",
                          );
                          return filtered.includes(item)
                            ? filtered.filter((c) => c !== item)
                            : [...filtered, item];
                        });
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px",
                      }}
                    >
                      <p style={{ margin: 0 }}>{item}</p>
                      {selectedChapter.includes(item) ? (
                        <img
                          src={assets.ic_checkbox_tick}
                          alt="selected"
                          style={{ width: "16px", height: "16px" }}
                        />
                      ) : (
                        <img
                          src={assets.ic_uncheckbox}
                          alt="not-selected"
                          style={{ width: "16px", height: "16px" }}
                        />
                      )}
                    </section>
                  ))
                ) : (
                  <p className="option_item">No chapters found</p>
                )}
              </section>
            )}
          </section>


          {/* LANGUAGE */}
          <section
            className="subject_wrapper"
            ref={languageRef}
            onClick={() => setOpenDropdown("language")}
          >
            <h4>Language</h4>
            <section className="drop_down_wrapper">
              <p>{selectedLanguage || "Select Language"}</p>
              <img src={assets.ic_chevron_down} alt="" />
            </section>
            {openDropdown === "language" && (
              <section className="options_list">
                {LANGUAGE_TN_STATE_BOARD.map((item, i) => (
                  <p
                    key={i}
                    className="option_item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLanguage(item);
                      setOpenDropdown(null);
                    }}
                  >
                    {item}
                  </p>
                ))}
              </section>
            )}
          </section>
          {!mode && (
            <button
              className="starts_chart_wrapper"
              disabled={!isStartEnabled}
              onClick={onClickStart}
            >
              Ask Question
            </button>
          )}
          {!mode && (
            <button
              className="history_wrapper_card"
              disabled={!isStartEnabled}
              onClick={getHistoryDetails}
            >
              <img
                src={assets.ic_history}
                alt="history"
                className="history_ic"
              />
              Previous Learnings
            </button>
          )}
          {mode && (
            <button
              className="history_wrapper"
              style={
                avatarSession
                  ? { backgroundColor: "#ef4444", color: "white" }
                  : {}
              }
              disabled={!isStartEnabled}
              onClick={avatarSession ? onEndAvatarInteraction : onLiveAvatarApi}
            >
              {avatarSession ? "End Interaction" : "Interact with Avatar"}
            </button>
          )}
        </section>
      </section>

      {/* CHAT PANEL */}
      <section className="chat_panel">
        <section className="profile_header_wrapper">
          <section className="profile_details_section">
            <img src={assets.ic_profile_moniter} alt="" />
            <div>
              <h4>{user?.data?.name ? user?.data?.name : "Rovian AI Tutor"}</h4>
              <h6>Online</h6>
            </div>
          </section>
          <section className="clear_wrapper">
            <button className="clear_chat_wrapper" onClick={handleClearChat}>
              <img src={assets.ic_refresh} alt="refresh" />
              Clear Chat
            </button>
            <button className="new_chat_card" onClick={handleNewChat}>
              <img src={assets.ic_add_circle} alt="refresh" />
              New Chat
            </button>
          </section>
        </section>

        <section
          className={`chat_body ${mode ? "full_width" : ""}`}
          ref={chatBodyRef}
        >
          {conversation.length === 0 && !isHistoryEmpty ? (
            <section className="welcome_content_wrapper">
              <section className="ai_content">
                <section className="ai_wrapper">
                  <img src={assets.ic_aitv} alt="aitv" />
                  <p>{mode ? "Live Avatar" : "Rovian AI Chatbot"}</p>
                </section>
                <section className="welcome_assist_wrapper">
                  <h2>Welcome to Your AI Learning Assistant!</h2>
                  <h5>
                    Choose what you want to learn today from the left panel. I'm
                    here to help you understand any topic, answer your
                    questions, and guide you through your studies.
                  </h5>
                </section>
              </section>
              <section className="welcome_card_wrapper">
                {WELCOME_CARD.map(renderWelComeCard)}
              </section>
              <section className="get_start">
                <h6>
                  👈 Get started by selecting your subject, chapter, and
                  language
                </h6>
              </section>
            </section>
          ) : mode && (avatarSession || isLiveAvatarLoading) ? (
            LiveAvatarView()
          ) : isHistoryEmpty ? (
            <section className="no_history_wrapper">
              <h4>No History Found</h4>
            </section>
          ) : (
            <Conversation
              convo={conversation}
              subject={selectedSubject}
              chapter={selectedChapter}
              mode={mode}
              medium={selectedMedium}
              part={selectedPart}
              unit={selectedUnit}
              lesson={selectedLesson}
            />
          )}
          <div ref={chatBottomRef} style={{ height: 0, flexShrink: 0 }} />
        </section>

        {!mode && isEnable && (
          <section className="chat_footer">
            <section
              className={`chat_input_wrapper ${isLoading ? "disabled" : ""}`}
            >
              <input
                placeholder={
                  isLoading ? "AI is thinking..." : "Ask me anything..."
                }
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={isLoading}
              />
              <section className="mic_wrapper">
                <img
                  src={assets.ic_mic}
                  alt="ic_mic"
                  className={`mic_icon ${isListening ? "listening" : ""} ${isLoading ? "disabled" : ""}`}
                  onClick={!isLoading ? handleMicClick : undefined}
                />
                <img
                  src={assets.ic_upload_btn}
                  alt="send"
                  className={`send_icon ${isLoading ? "disabled" : ""}`}
                  onClick={!isLoading ? handleSend : undefined}
                  disabled={!isEnable || isLoading}
                />
              </section>
            </section>
          </section>
        )}
      </section>
    </section>
  );
};
